import { Injectable } from '@angular/core';
import { Observable, of, combineLatest, ReplaySubject } from 'rxjs';
import { map, catchError, tap, shareReplay } from 'rxjs/operators';
import { CashRegisterService } from './cash-register.service';
import { forceCreateReceiptMenuStatus } from './receipt-force-create.util';
import { ReceiptsService, ReceiptType } from './receipts.service';
import { IntegrationEntityTypes } from '@interfaces/integration-sync-metadata';

export interface SyncButtonConfig {
  id: string;
  label: string;
  icon: string;
  successMessage: string;
  errorMessage: string;
  actionObservable: Observable<any>;
  statusObservable: Observable<any>;
  isEnabled: boolean;
}

export enum SyncTarget {
  CASH_REGISTER = 'CASH_REGISTER',
  FISCALITY = 'FISCALITY'
}

export interface SyncHelperOptions {
  /** When true, manual receipt sync skips auto-receipt flags and enables WS-3360 payment validation. */
  bypassAutoReceiptFlags?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SyncHelperService {
  private _isCashRegisterIntegrated: boolean = false;
  private _isFiscalityIntegrated: boolean = false;
  
  // Observable which emits true when all integrations are loaded
  private integrationsLoaded$ = new ReplaySubject<boolean>(1);
  
  // Observable cached per integration
  private cashRegisterIntegration$: Observable<boolean>;
  private fiscalityIntegration$: Observable<boolean>;

  constructor(
    private cashRegisterService: CashRegisterService,
    private receiptsService: ReceiptsService
  ) {
    // Observable cache with shareReplay for avoid multiple requests
    this.cashRegisterIntegration$ = this.cashRegisterService.isIntegrated().pipe(
      tap(isIntegrated => this._isCashRegisterIntegrated = isIntegrated),
      shareReplay(1),
      catchError(() => of(false))
    );
    
    this.fiscalityIntegration$ = this.receiptsService.isFiscalityIntegrated().pipe(
      tap(isIntegrated => this._isFiscalityIntegrated = isIntegrated),
      shareReplay(1),
      catchError(() => of(false))
    );
    
    this.initializeIntegrationChecks();
  }

  private initializeIntegrationChecks() {
    // Wait for all integrations to be loaded
    combineLatest([
      this.cashRegisterIntegration$,
      this.fiscalityIntegration$
    ]).subscribe({
      next: () => {
        this.integrationsLoaded$.next(true);
        this.integrationsLoaded$.complete();
      },
      error: () => {
        this.integrationsLoaded$.next(true);
        this.integrationsLoaded$.complete();
      }
    });
  }

  get isCashRegisterIntegrated(): boolean {
    return this._isCashRegisterIntegrated;
  }

  get isFiscalityIntegrated(): boolean {
    return this._isFiscalityIntegrated;
  }

  /**
   * Get all available sync button configurations for an entity
   * Returns an Observable that emits when integrations are loaded
   */
  getAvailableSyncButtons$(
    wineryId: string,
    entityType: IntegrationEntityTypes,
    entityId: string,
    entityEmail?: string,
    options?: SyncHelperOptions
  ): Observable<SyncButtonConfig[]> {
    return this.integrationsLoaded$.pipe(
      map(() => this.getAvailableSyncButtons(wineryId, entityType, entityId, entityEmail, options))
    );
  }

  /**
   * Get all available sync button configurations for an entity (sync version)
   * Use only if you're sure integrations are already loaded
   */
  getAvailableSyncButtons(
    wineryId: string,
    entityType: IntegrationEntityTypes,
    entityId: string,
    entityEmail?: string,
    options?: SyncHelperOptions
  ): SyncButtonConfig[] {
    const buttons: SyncButtonConfig[] = [];
    
    const cashRegisterButton = this.getSyncButtonConfig(
      SyncTarget.CASH_REGISTER, 
      wineryId, 
      entityType, 
      entityId, 
      entityEmail,
      options
    );
    if (cashRegisterButton) {
      buttons.push(cashRegisterButton);
    }

    const supportsReceipts = [
      IntegrationEntityTypes.ORDER, 
      IntegrationEntityTypes.RESERVATION, 
      IntegrationEntityTypes.GIFT, 
      IntegrationEntityTypes.PREORDER
    ].includes(entityType);
    
    if (supportsReceipts) {
      const receiptButton = this.getSyncButtonConfig(
        SyncTarget.FISCALITY, 
        wineryId, 
        entityType, 
        entityId, 
        entityEmail,
        options
      );
      if (receiptButton) {
        buttons.push(receiptButton);
      }
    }

    return buttons;
  }

  /**
   * Get sync button configuration for a specific object
   * @param syncTarget - Type of sync (CASH_REGISTER or FISCALITY)
   * @param wineryId - Winery ID
   * @param entityType - Type of entity (ORDER, RESERVATION, GIFT, PREORDER, PRODUCT, CONTACT)
   * @param entityId - ID of the entity (for CONTACT, this should be email)
   * @param entityEmail - Optional email override for contact entities
   * @returns SyncButtonConfig or null if not available
   */
  getSyncButtonConfig(
    syncTarget: SyncTarget,
    wineryId: string,
    entityType: IntegrationEntityTypes,
    entityId: string,
    entityEmail?: string,
    options?: SyncHelperOptions
  ): SyncButtonConfig | null {
    if (syncTarget === SyncTarget.CASH_REGISTER && !this._isCashRegisterIntegrated) {
      return null;
    }
    
    if (syncTarget === SyncTarget.FISCALITY && !this._isFiscalityIntegrated) {
      return null;
    }

    const config = this.getSyncConfigForEntity(wineryId, entityType, entityId, entityEmail, options);
    
    if (!config) {
      return null;
    }

    switch (syncTarget) {
      case SyncTarget.CASH_REGISTER:
        return {
          id: 'cash-register-sync',
          label: 'Sync with Cash register',
          icon: 'currency_exchange',
          successMessage: 'Cash register synced successfully',
          errorMessage: 'Error syncing cash register, please try again',
          actionObservable: config.cashRegisterSync,
          statusObservable: config.cashRegisterStatus,
          isEnabled: this._isCashRegisterIntegrated
        };
      
      case SyncTarget.FISCALITY:
        return {
          id: 'force-create-receipt',
          label: 'Force create receipt',
          icon: 'receipt_long',
          successMessage: 'Receipt creation launched successfully',
          errorMessage: 'Error creating receipt, please try again',
          actionObservable: config.receiptSync,
          statusObservable: config.receiptStatus,
          isEnabled: this._isFiscalityIntegrated
        };
      
      default:
        return null;
    }
  }

  private receiptForceCreateStatus(externalEntityId: string, receiptType: ReceiptType) {
    return this.receiptsService
      .getByEntity(externalEntityId, receiptType)
      .pipe(map((receipts) => forceCreateReceiptMenuStatus(receipts)));
  }

  private getSyncConfigForEntity(
    wineryId: string,
    entityType: IntegrationEntityTypes,
    entityId: string,
    entityEmail?: string,
    options?: SyncHelperOptions
  ): {
    cashRegisterSync: Observable<any>;
    cashRegisterStatus: Observable<any>;
    receiptSync: Observable<any>;
    receiptStatus: Observable<any>;
  } | null {
    const bypassAutoReceiptFlags = options?.bypassAutoReceiptFlags === true;

    switch (entityType) {
      case IntegrationEntityTypes.ORDER:
        return {
          cashRegisterSync: this.cashRegisterService.syncOrder(wineryId, entityId),
          cashRegisterStatus: this.cashRegisterService.getMetadata(entityType, entityId),
          receiptSync: this.receiptsService.forceCreateReceipt(entityId, ReceiptType.ORDER, bypassAutoReceiptFlags),
          receiptStatus: this.receiptForceCreateStatus(entityId, ReceiptType.ORDER)
        };
      
      case IntegrationEntityTypes.RESERVATION:
        return {
          cashRegisterSync: this.cashRegisterService.syncReservation(wineryId, entityId),
          cashRegisterStatus: this.cashRegisterService.getMetadata(entityType, entityId),
          receiptSync: this.receiptsService.forceCreateReceipt(entityId, ReceiptType.RESERVATION, bypassAutoReceiptFlags),
          receiptStatus: this.receiptForceCreateStatus(entityId, ReceiptType.RESERVATION)
        };
      
      case IntegrationEntityTypes.GIFT:
        return {
          cashRegisterSync: this.cashRegisterService.syncGift(wineryId, entityId),
          cashRegisterStatus: this.cashRegisterService.getMetadata(entityType, entityId),
          receiptSync: this.receiptsService.forceCreateReceipt(entityId, ReceiptType.GIFT, bypassAutoReceiptFlags),
          receiptStatus: this.receiptForceCreateStatus(entityId, ReceiptType.GIFT)
        };
      
      case IntegrationEntityTypes.PREORDER:
        return {
          cashRegisterSync: this.cashRegisterService.syncPreOrder(wineryId, entityId),
          cashRegisterStatus: this.cashRegisterService.getMetadata(entityType, entityId),
          receiptSync: this.receiptsService.forceCreateReceipt(entityId, ReceiptType.PREORDER, bypassAutoReceiptFlags),
          receiptStatus: this.receiptForceCreateStatus(entityId, ReceiptType.PREORDER)
        };
      
      case IntegrationEntityTypes.PRODUCT:
        return {
          cashRegisterSync: this.cashRegisterService.syncProduct(wineryId, entityId),
          cashRegisterStatus: this.cashRegisterService.getMetadata(entityType, entityId),
          receiptSync: of(null).pipe(map(() => ({}))), // Products don't have receipts
          receiptStatus: of([])
        };
      
      case IntegrationEntityTypes.CONTACT:
        if (!entityEmail) {
          return null;
        }
        return {
          cashRegisterSync: this.cashRegisterService.syncContact(wineryId, entityEmail),
          cashRegisterStatus: this.cashRegisterService.getMetadata(entityType, entityId),
          receiptSync: of(null).pipe(map(() => ({}))), // Contacts don't have receipts
          receiptStatus: of([])
        };
      
      default:
        return null;
    }
  }
}

